/**
 * OptimizationsPage — top-level page listing past Grid Search and Walk-Forward runs.
 *
 * - Tabs: Grid Search | Walk-Forward
 * - Filters: Strategy, Symbol, Timeframe text inputs + Clear button
 * - Sortable table: Date, Strategy, Symbol, Timeframe, Sharpe, Return, Max DD, Trades, Combos
 * - Pagination (limit/offset)
 * - Row click → inline detail view
 * - Empty / loading / error states
 */

import { useState, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchOptimizations, fetchOptimizationDetail } from '../../api/client.js';
import { OptimizationDetail } from './OptimizationDetail.js';
import { useOptimizationsPageStore } from '../../stores/optimizationsPageStore.js';
import { useConfigurationStore } from '../../stores/configurationStore.js';
import { usePaperTradingStore } from '../../stores/paperTradingStore.js';
import { usePortfoliosStore } from '../../stores/portfoliosStore.js';
import { RouteLink } from '../RouteLink/RouteLink.js';
import { optimizationsTabToSlug } from '../../hooks/useUrlSync.js';
import type {
  OptimizationSummary,
  OptimizationDetail as OptimizationDetailType,
  OptimizationsQueryParams,
} from '../../types/optimizations.js';

// ── constants ──────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;
type OptType = 'grid_search' | 'walk_forward';
type SortField = 'sharpeRatio' | 'totalReturnPercent' | 'optimizedAt';
type SortDir = 'asc' | 'desc';

// ── helper formatters ──────────────────────────────────────────────────────────

function fmt2(n: number): string {
  return n.toFixed(2);
}

function fmtDate(ms: number): string {
  return new Date(ms).toLocaleDateString();
}

// ── tab button ─────────────────────────────────────────────────────────────────

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function TabButton({ active, onClick, children }: TabButtonProps) {
  return (
    <button
      role="button"
      onClick={onClick}
      className={[
        'px-3.5 py-1.5 text-[13px] rounded-md border transition-colors duration-100 whitespace-nowrap',
        active
          ? 'font-semibold text-gray-200 bg-blue-950 border-blue-600'
          : 'font-normal text-gray-500 bg-transparent border-gray-700 hover:text-gray-300 hover:border-gray-500',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

// ── sortable column header ─────────────────────────────────────────────────────

interface SortableThProps {
  field: SortField;
  label: string;
  currentSort: SortField;
  currentDir: SortDir;
  onSort: (f: SortField) => void;
}

function SortableTh({ field, label, currentSort, currentDir, onSort }: SortableThProps) {
  const active = currentSort === field;
  return (
    <th
      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 cursor-pointer select-none hover:text-gray-300 whitespace-nowrap"
      onClick={() => onSort(field)}
    >
      {label}
      {active && (
        <span className="ml-1 text-gray-400">{currentDir === 'asc' ? '↑' : '↓'}</span>
      )}
    </th>
  );
}

// ── main component ─────────────────────────────────────────────────────────────

export function OptimizationsPage() {
  // activeTab is now stored in Zustand so useUrlSync can sync it to the URL.
  const activeTab = useOptimizationsPageStore((s) => s.activeTab);
  const setActiveTabInStore = useOptimizationsPageStore((s) => s.setActiveTab);
  // selectedOptimizationId is also in Zustand so the detail view is URL-navigable.
  const selectedId = useOptimizationsPageStore((s) => s.selectedOptimizationId);
  const setSelectedId = useOptimizationsPageStore((s) => s.setSelectedOptimizationId);
  // SHA-23: set by useUrlSync when the URL couldn't be parsed into a valid
  // /optimizations route (e.g. a bare /optimizations/<runId> deep link missing
  // the tab slug) — render a graceful not-found panel instead of crashing.
  const routeNotFoundPath = useOptimizationsPageStore((s) => s.routeNotFoundPath);
  const setRouteNotFoundPath = useOptimizationsPageStore((s) => s.setRouteNotFoundPath);

  // Owner navigation — navigate to the owning strategy or portfolio.
  const setSelectedStrategyId = useConfigurationStore((s) => s.setSelectedStrategyId);
  const setSelectedPortfolioId = usePortfoliosStore((s) => s.setSelectedPortfolioId);
  const setActivePage = usePaperTradingStore((s) => s.setActivePage);
  const [filterStrategy, setFilterStrategy] = useState('');
  const [filterSymbol, setFilterSymbol] = useState('');
  const [filterTimeframe, setFilterTimeframe] = useState('');
  const [offset, setOffset] = useState(0);
  const [sortField, setSortField] = useState<SortField>('optimizedAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Build query params
  const queryParams: OptimizationsQueryParams = {
    type: activeTab,
    limit: PAGE_SIZE,
    offset,
    ...(filterStrategy ? { strategyName: filterStrategy } : {}),
    ...(filterSymbol ? { symbol: filterSymbol } : {}),
    ...(filterTimeframe ? { timeframe: filterTimeframe } : {}),
  };

  const {
    data: listData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['optimizations', activeTab, filterStrategy, filterSymbol, filterTimeframe, offset],
    queryFn: () => fetchOptimizations(queryParams),
  });

  const {
    data: detailData,
    isLoading: isDetailLoading,
    isError: isDetailError,
  } = useQuery({
    queryKey: ['optimization-detail', selectedId],
    queryFn: () => {
      if (!selectedId) throw new Error('No id selected');
      return fetchOptimizationDetail(selectedId);
    },
    enabled: !!selectedId,
    // Don't retry on failure — a 404 for a stale/deleted id should fall back to list.
    retry: false,
  });

  // Sort locally (server doesn't guarantee order in this iteration)
  const items: OptimizationSummary[] = listData?.items ?? [];
  const sortedItems = [...items].sort((a, b) => {
    let aVal: number;
    let bVal: number;
    if (sortField === 'sharpeRatio') {
      aVal = a.bestMetrics.sharpeRatio;
      bVal = b.bestMetrics.sharpeRatio;
    } else if (sortField === 'totalReturnPercent') {
      aVal = a.bestMetrics.totalReturnPercent;
      bVal = b.bestMetrics.totalReturnPercent;
    } else {
      aVal = a.optimizedAt;
      bVal = b.optimizedAt;
    }
    return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
  });

  const total = listData?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  // Reset offset when tab or filters change; also clear the detail selection so
  // switching tabs doesn't carry a stale detail id into the new tab's list view.
  const handleTabChange = useCallback((tab: OptType) => {
    setActiveTabInStore(tab);
    setOffset(0);
    setSelectedId(null);
  }, [setActiveTabInStore, setSelectedId]);

  const handleClearFilters = useCallback(() => {
    setFilterStrategy('');
    setFilterSymbol('');
    setFilterTimeframe('');
    setOffset(0);
  }, []);

  const handleSort = useCallback((field: SortField) => {
    if (sortField === field) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  }, [sortField]);

  // Reset offset on filter changes
  useEffect(() => {
    setOffset(0);
  }, [filterStrategy, filterSymbol, filterTimeframe]);

  // ── not-found view (SHA-23) ─────────────────────────────────────────────────
  // The URL couldn't be parsed into a valid /optimizations route (e.g. a bare
  // /optimizations/<runId> deep link missing the tab slug). Show a graceful
  // panel instead of the router throwing and white-screening the whole app.

  if (routeNotFoundPath) {
    return (
      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        <div role="alert" className="max-w-lg mx-auto mt-12 text-center space-y-3">
          <h1 className="text-lg font-semibold text-gray-200">Optimization run not found</h1>
          <p className="text-sm text-gray-400">
            The link <code className="text-gray-300 bg-gray-800 px-1 py-0.5 rounded">{routeNotFoundPath}</code>{' '}
            is missing or has an invalid tab (expected <code className="text-gray-300">grid-search</code> or{' '}
            <code className="text-gray-300">walk-forward</code>).
          </p>
          <button
            onClick={() => setRouteNotFoundPath(null)}
            className="px-4 py-2 text-sm font-medium text-blue-300 bg-blue-950 border border-blue-700 rounded-lg hover:bg-blue-900 transition-colors"
          >
            Back to Optimizations
          </button>
        </div>
      </div>
    );
  }

  // ── detail view ──────────────────────────────────────────────────────────────

  if (selectedId) {
    // If the fetch failed (e.g. stale/deleted run), fall back to list and clear the
    // URL-synced id — don't strand the user on a broken detail view.
    if (isDetailError) {
      setSelectedId(null);
      // Fall through to render the list view below.
    } else if (isDetailLoading || !detailData) {
      return (
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSelectedId(null)}
              className="flex items-center gap-1 text-sm text-gray-400 hover:text-white transition-colors"
              aria-label="Back to list"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
            <div role="status" className="flex items-center gap-2 text-sm text-gray-400">
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Loading details…
            </div>
          </div>
        </div>
      );
    } else {
      const detail = detailData as OptimizationDetailType;
      return (
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          <OptimizationDetail detail={detail} onBack={() => setSelectedId(null)} />
        </div>
      );
    }
  }

  // ── list view ────────────────────────────────────────────────────────────────

  const typeLabel = activeTab === 'grid_search' ? 'Grid Search' : 'Walk-Forward';

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
      {/* Page title */}
      <div>
        <h1 className="text-xl font-bold text-white">Optimizations</h1>
        <p className="text-sm text-gray-400 mt-0.5">Past grid search and walk-forward validation runs</p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2">
        <TabButton active={activeTab === 'grid_search'} onClick={() => handleTabChange('grid_search')}>
          Grid Search
        </TabButton>
        <TabButton active={activeTab === 'walk_forward'} onClick={() => handleTabChange('walk_forward')}>
          Walk-Forward
        </TabButton>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          placeholder="Filter by strategy"
          value={filterStrategy}
          onChange={e => setFilterStrategy(e.target.value)}
          className="px-3 py-1.5 text-sm bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-500 focus:outline-none focus:border-blue-500 w-44"
        />
        <input
          type="text"
          placeholder="Filter by symbol"
          value={filterSymbol}
          onChange={e => setFilterSymbol(e.target.value)}
          className="px-3 py-1.5 text-sm bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-500 focus:outline-none focus:border-blue-500 w-44"
        />
        <input
          type="text"
          placeholder="Filter by timeframe"
          value={filterTimeframe}
          onChange={e => setFilterTimeframe(e.target.value)}
          className="px-3 py-1.5 text-sm bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-500 focus:outline-none focus:border-blue-500 w-44"
        />
        {(filterStrategy || filterSymbol || filterTimeframe) && (
          <button
            onClick={handleClearFilters}
            className="px-3 py-1.5 text-sm text-gray-400 hover:text-white border border-gray-600 rounded-lg hover:border-gray-400 transition-colors"
          >
            Clear
          </button>
        )}
        {total > 0 && (
          <span className="text-sm text-gray-500 ml-auto">
            {total} {total === 1 ? 'run' : 'runs'}
          </span>
        )}
      </div>

      {/* Loading state */}
      {isLoading && (
        <div role="status" className="flex items-center gap-2 py-8 text-gray-400">
          <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          Loading {typeLabel} runs…
        </div>
      )}

      {/* Error state */}
      {isError && !isLoading && (
        <div role="alert" className="rounded-lg bg-red-900/30 border border-red-700 p-4 text-sm text-red-300">
          Failed to load {typeLabel} runs:{' '}
          {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !isError && sortedItems.length === 0 && (
        <div className="py-16 text-center text-gray-500">
          <svg className="w-12 h-12 mx-auto mb-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          {activeTab === 'grid_search'
            ? 'No grid search runs yet. Run a grid search from the optimizer.'
            : 'No walk-forward runs yet. Run walk-forward validation from the optimizer.'}
        </div>
      )}

      {/* Table */}
      {!isLoading && !isError && sortedItems.length > 0 && (
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-700 bg-gray-800/60">
                <SortableTh field="optimizedAt" label="Date" currentSort={sortField} currentDir={sortDir} onSort={handleSort} />
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Strategy</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Symbol</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">TF</th>
                <SortableTh field="sharpeRatio" label="Sharpe" currentSort={sortField} currentDir={sortDir} onSort={handleSort} />
                <SortableTh field="totalReturnPercent" label="Return %" currentSort={sortField} currentDir={sortDir} onSort={handleSort} />
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Max DD %</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Trades</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Combos</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((item, idx) => (
                <tr
                  key={item.id}
                  data-testid={`optimization-row-${item.id}`}
                  onClick={() => setSelectedId(item.id)}
                  className={[
                    // `relative` makes this <tr> the containing block for the
                    // row-anchor's `after:` overlay below (position:absolute
                    // resolves against the nearest positioned ancestor,
                    // regardless of how deep the anchor is nested in the DOM —
                    // it doesn't have to be a direct child).
                    'relative border-b border-gray-700/50 hover:bg-gray-700/40 cursor-pointer transition-colors',
                    idx % 2 === 1 ? 'bg-gray-800/30' : '',
                  ].join(' ')}
                >
                  <td className="px-4 py-3 text-gray-400 whitespace-nowrap">
                    {/*
                      SHA-39: a real <a href> that acts as the whole row's
                      "open" target — its `after:` pseudo-element is stretched
                      to cover the entire <tr> (containing block = the
                      `relative` <tr> above), so cmd/ctrl+click, middle-click,
                      and right-click "Open in new tab" work no matter where in
                      the row the user clicks, not just on the date cell.
                      The <tr onClick> above remains as the plain-click SPA-nav
                      fallback (also what jsdom/RTL exercises, since jsdom does
                      not do real hit-testing of overlapping/z-indexed elements).
                      Row-action buttons (Strategy:/Portfolio: owner links)
                      keep `relative z-10` so they still resolve above this
                      overlay for real click hit-testing, and keep their own
                      stopPropagation so they never bubble into the row select.
                    */}
                    <span onClick={(e) => e.stopPropagation()}>
                      <RouteLink
                        href={`/optimizations/${optimizationsTabToSlug(item.type)}/${encodeURIComponent(item.id)}`}
                        onNavigate={() => setSelectedId(item.id)}
                        aria-label={`Open ${item.strategyName} optimization run from ${fmtDate(item.optimizedAt)}`}
                        className="block text-gray-400 hover:text-gray-200 hover:underline after:content-[''] after:absolute after:inset-0"
                      >
                        {fmtDate(item.optimizedAt)}
                      </RouteLink>
                    </span>
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-200 max-w-[220px]">
                    <span className="truncate block">{item.strategyName}</span>
                    {/* Migration 065: owner back-link (strategy or portfolio) */}
                    {item.ownerType === 'strategy' && item.ownerId && item.ownerName && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedStrategyId(item.ownerId!);
                          setActivePage('configurations');
                        }}
                        className="relative z-10 mt-0.5 text-[11px] text-blue-400 hover:text-blue-300 hover:underline leading-none"
                        title="Open this strategy in the Strategies page"
                      >
                        Strategy: {item.ownerName}
                      </button>
                    )}
                    {item.ownerType === 'aggregation' && item.ownerId && item.ownerName && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedPortfolioId(item.ownerId!);
                          setActivePage('portfolios');
                        }}
                        className="relative z-10 mt-0.5 text-[11px] text-purple-400 hover:text-purple-300 hover:underline leading-none"
                        title="Open this portfolio in the Portfolios page"
                      >
                        Portfolio: {item.ownerName}
                      </button>
                    )}
                    {/* Legacy fallback for rows without migration 065 owner fields */}
                    {!item.ownerType && item.userStrategyId && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedStrategyId(item.userStrategyId!);
                          setActivePage('configurations');
                        }}
                        className="relative z-10 mt-0.5 text-[11px] text-blue-400 hover:text-blue-300 hover:underline leading-none"
                        title="Open this strategy in the Strategies page"
                      >
                        View strategy
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-300">{item.symbol}</td>
                  <td className="px-4 py-3 text-gray-400">{item.timeframe}</td>
                  <td className={`px-4 py-3 font-semibold tabular-nums ${item.bestMetrics.sharpeRatio >= 1 ? 'text-green-400' : item.bestMetrics.sharpeRatio >= 0 ? 'text-yellow-400' : 'text-red-400'}`}>
                    {fmt2(item.bestMetrics.sharpeRatio)}
                  </td>
                  <td className={`px-4 py-3 font-semibold tabular-nums ${item.bestMetrics.totalReturnPercent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {fmt2(item.bestMetrics.totalReturnPercent)}%
                  </td>
                  <td className="px-4 py-3 text-red-400 tabular-nums">{fmt2(item.bestMetrics.maxDrawdownPercent)}%</td>
                  <td className="px-4 py-3 text-gray-300 tabular-nums">{item.bestMetrics.totalTrades}</td>
                  <td className="px-4 py-3 text-gray-400 tabular-nums">
                    {item.testedCombinations}/{item.totalCombinations}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!isLoading && totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">
            Page {currentPage} of {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <button
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              className="px-3 py-1.5 text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <button
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
              className="px-3 py-1.5 text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
